import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
  useCallback,
} from "react";
import CustomerConfigService from "../services/CustomerConfigService";
import { CustomerConfigRequest } from "../models/Requests/CustomerConfigRequest";
import { GetCustomerConfigResponse } from "../models/Responses/CustomerConfigResponse";
import { useAppContext } from "./AppContext";
import { useUserProfileContext } from "./UserProfileContext";
import { FORECAST_CONSTANTS } from "../constants/forecastConstants";
import { extractErrorMessage, safeErrorLog } from "../utils/errorUtils";

export interface ForecastSettings {
  horizon_days: number;
  past_usage_days: number;
  moving_average_window: number;
  confidence_level: number;
  algorithm_type: string;
  time_zone: string;
  auto_reorder: boolean;
  safety_stock_multiplier: number;
  lead_time_days: number;
  min_order_quantity: number;
  notification_enabled: boolean;
  email_reports: boolean;
}

export interface ISettingsContextType {
  settings: ForecastSettings;
  updateSettings: (newSettings: ForecastSettings) => Promise<void>;
  resetSettings: () => void;
  isModified: boolean;
  setIsModified: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
  saveToApi: (
    settingsToSave?: ForecastSettings,
  ) => Promise<{ success: boolean; errorMessage?: string }>;
  loadSettingsFromApi: (forceReload?: boolean) => Promise<void>;
}

const DEFAULT_SETTINGS: ForecastSettings = {
  horizon_days: FORECAST_CONSTANTS.DEFAULT_FORECAST_DAYS,
  past_usage_days: FORECAST_CONSTANTS.DEFAULT_PAST_USAGE_DAYS,
  moving_average_window: 7,
  confidence_level: 95,
  algorithm_type: "simple_moving_algorithm",
  time_zone: FORECAST_CONSTANTS.DEFAULT_TIMEZONE,
  auto_reorder: true,
  safety_stock_multiplier: 1.5,
  lead_time_days: 3,
  min_order_quantity: 1,
  notification_enabled: true,
  email_reports: false,
};

// eslint-disable-next-line react-refresh/only-export-components
export const SettingsContext = createContext<ISettingsContextType | undefined>(
  undefined,
);

const useSettingsContext = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error(
      "useSettingsContext must be used within a SettingsProvider",
    );
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  // Clear any existing localStorage data for forecast_settings
  useEffect(() => {
    localStorage.removeItem("forecast_settings");
  }, []);

  // Initialize settings with defaults (no localStorage)
  const [settings, setSettings] = useState<ForecastSettings>(DEFAULT_SETTINGS);

  const [isModified, setIsModified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Get user context for API calls
  const { userContextData } = useAppContext();
  const { user } = useUserProfileContext();

  const updateSettings = async (newSettings: ForecastSettings) => {
    setSettings(newSettings);
    setIsModified(false);
    setError(null);
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
    setIsModified(false);
    setError(null);
  };

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const saveToApi = useCallback(
    async (
      settingsToSave?: ForecastSettings,
    ): Promise<{ success: boolean; errorMessage?: string }> => {
      if (!userContextData || !user) {
        const errorMessage = "User context not available";
        setError(errorMessage);
        return { success: false, errorMessage };
      }

      setIsLoading(true);
      setError(null);

      try {
        // Use provided settings or fall back to context settings
        const settingsToUse = settingsToSave || settings;

        // Map settings to API payload
        const configPayload: CustomerConfigRequest = {
          algorithm: settingsToUse.algorithm_type,
          forecast_days: settingsToUse.horizon_days,
          past_usage_days: settingsToUse.past_usage_days,
          time_zone: settingsToUse.time_zone,
        };

        const response =
          await CustomerConfigService.saveCustomerConfig(configPayload);

        if (response.status === "success") {
          // Update context settings if they were provided
          if (settingsToSave) {
            setSettings(settingsToSave);
          }
          setIsModified(false);
          return { success: true };
        } else {
          const errorMessage = extractErrorMessage(
            { response: { data: response } },
            { defaultMessage: "Failed to save settings to API" },
          );
          setError(errorMessage);
          return { success: false, errorMessage };
        }
      } catch (err: unknown) {
        safeErrorLog("SettingsContext.saveToApi", err);
        const errorMessage = extractErrorMessage(err, {
          defaultMessage: "Failed to save settings to API",
        });
        setError(errorMessage);
        return { success: false, errorMessage };
      } finally {
        setIsLoading(false);
      }
    },
    [userContextData, user, settings, setIsModified, setError, setIsLoading],
  );

  const loadSettingsFromApi = useCallback(
    async (forceReload: boolean = false): Promise<void> => {
      if (!userContextData || !user || (isInitialized && !forceReload)) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Fetch settings from API
        const apiResponse: GetCustomerConfigResponse =
          await CustomerConfigService.getCustomerConfig();

        // Check if we have data and it's successful
        let apiSettings: ForecastSettings = { ...DEFAULT_SETTINGS };

        if (
          apiResponse.status === "success" &&
          apiResponse.data &&
          apiResponse.data.length > 0
        ) {
          // Get the first config record (assuming one per user/practice)
          const configData = apiResponse.data[0];

          if (configData) {
            apiSettings = {
              horizon_days:
                configData.ForecastDays || DEFAULT_SETTINGS.horizon_days,
              past_usage_days:
                configData.PastUsageDays || DEFAULT_SETTINGS.past_usage_days,
              moving_average_window: DEFAULT_SETTINGS.moving_average_window,
              confidence_level: DEFAULT_SETTINGS.confidence_level,
              algorithm_type:
                configData.Algorithm || DEFAULT_SETTINGS.algorithm_type,
              time_zone: configData.TimeZone || DEFAULT_SETTINGS.time_zone,
              auto_reorder: DEFAULT_SETTINGS.auto_reorder,
              safety_stock_multiplier: DEFAULT_SETTINGS.safety_stock_multiplier,
              lead_time_days: DEFAULT_SETTINGS.lead_time_days,
              min_order_quantity: DEFAULT_SETTINGS.min_order_quantity,
              notification_enabled: DEFAULT_SETTINGS.notification_enabled,
              email_reports: DEFAULT_SETTINGS.email_reports,
            };
          }
        }

        setSettings(apiSettings);
        setIsModified(false);
        setIsInitialized(true);
      } catch (err: unknown) {
        safeErrorLog("SettingsContext.loadSettingsFromApi", err);

        // Check if it's a 404 error or similar - use default values
        if (err && typeof err === "object" && "response" in err) {
          const axiosError = err as { response?: { status?: number } };
          if (axiosError.response?.status === 404) {
            setSettings(DEFAULT_SETTINGS);
          } else {
            setSettings(DEFAULT_SETTINGS);
          }
        } else {
          setSettings(DEFAULT_SETTINGS);
        }

        setIsModified(false);
        setIsInitialized(true);
      } finally {
        setIsLoading(false);
      }
    },
    [userContextData, user, isInitialized],
  );

  // Load settings from API when user context is available
  useEffect(() => {
    if (userContextData && user && !isInitialized) {
      loadSettingsFromApi();
    }
  }, [userContextData, user, isInitialized, loadSettingsFromApi]);

  const contextValue = useMemo<ISettingsContextType>(
    () => ({
      settings,
      updateSettings,
      resetSettings,
      isModified,
      setIsModified,
      isLoading,
      error,
      clearError,
      saveToApi,
      loadSettingsFromApi,
    }),
    [
      settings,
      isModified,
      isLoading,
      error,
      clearError,
      saveToApi,
      loadSettingsFromApi,
    ],
  );

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export { SettingsProvider, useSettingsContext };
